/**
 * Timeout + linear-backoff retry for mount-critical fetches.
 *
 * Use only when a fetch's failure leaves the UI permanently stuck (page-
 * level loading spinner, no recovery affordance). axios has no default
 * timeout, so a network hang blocks the await forever — `Promise.race`
 * here converts a hang into a rejection the retry can act on. Don't use
 * for action-handler fetches; the user can re-click those.
 *
 * Default: 3 attempts, 10s timeout each, 500ms × attempt# backoff.
 * `fn` is a thunk so each attempt re-invokes the underlying request.
 */
export async function fetchWithTimeoutAndRetry(fn, opts = {}) {
  const { timeoutMs = 10_000, maxAttempts = 3, backoffMs = 500 } = opts
  const withTimeout = (promise, ms) =>
    Promise.race([
      promise,
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error(`fetch timed out after ${ms}ms`)), ms)
      }),
    ])

  let lastError = null
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await withTimeout(fn(), timeoutMs)
    } catch (error) {
      lastError = error
      if (attempt < maxAttempts) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => { setTimeout(resolve, attempt * backoffMs) })
      }
    }
  }
  throw lastError
}
